import React, { useEffect, useState } from 'react';
import {debounce, filter, orderBy, reject, cloneDeep} from 'lodash';
import {formatDistance} from 'date-fns';

import Snackbar from '@material-ui/core/Snackbar';
import Slide from '@material-ui/core/Slide';
import Alert from '@material-ui/lab/Alert';
import CheckIcon from '@material-ui/icons/CheckCircle';
import CloseIcon from '@material-ui/icons/Cancel';
import SearchIcon from '@material-ui/icons/Search';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { green, red } from '@material-ui/core/colors';

import {onlineHubHost} from '../../config';
import { useFetchResource } from '../../helpers/hooks';

import IconInput from '../IconInput/IconInput';
import Table from '../Table';

import './DeviceList.css';

function SlideTransition(props) {
  return <Slide {...props} direction="up" />;
}

const columns = [
  {
    id: 'online',
    label: 'Online',
    minWidth: 50,
    align: 'left',
    sort: true,
    format: (value) => value ? <CheckIcon style={{color: green[500]}} /> : <CloseIcon style={{color: red[500]}} />,
  },
  { id: 'name', label: 'Name', sort: true, minWidth: 170 },
  { id: 'hostname', label: 'Hostname', sort: true, minWidth: 80 },
  { id: 'macAddress', label: 'Mac Address', sort: false, minWidth: 100 },
  { id: 'serialNumber', label: 'Serial Number', sort: false, minWidth: 100 },
  { id: 'product', label: 'Product', sort: true, minWidth: 80 },
  {
    id: 'nextServer',
    label: 'Next Server',
    sort: 'true',
    minWidth: 120,
    format: value => value && value.name
  },
  {
    id: 'bsnSetup',
    label: 'BSN Setup',
    sort: true,
    minWidth: 170,
    align: 'center',
    format: value => value ? (
      <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
        <span>{value.network}</span>
        <span style={{paddingTop: '10px'}}>{value.name}</span>
      </div>
    ) : null
  }
];

const managedColumns = cloneDeep(columns);
managedColumns[6].id = 'servers';
managedColumns[6].label = 'Last server';
managedColumns[6].format = value => {
  if (value.length === 0) return '';
  const server = value[value.length - 1];
  if (server.payload && server.payload.userId) {
    return (<div>
      <span>{server.name}</span><br/>
      <span>{formatDistance(new Date(), new Date(server.manageDate))} ago, by <a target="_blank" rel="noopener noreferrer" href={`https://${server.name}/api/users/${server.payload.userId}`}>{server.payload.userId.substring(16)}</a>.</span>
    </div>);
  }
  return (<div>
    <span>{server.name}</span><br/>
    <span>{formatDistance(new Date(), new Date(server.manageDate))} ago</span>
  </div>);
};

function sortItems(items, sortBy, sortOrder) {
  if (sortBy === 'online') {
    return orderBy(items, [item => item.online.toString()], [sortOrder]);
  }
  if (sortBy === 'nextServer') {
    return orderBy(items, [item => item.nextServer && item.nextServer.name], [sortOrder]);
  }
  if (sortBy === 'servers') {
    return orderBy(items, [item => item.servers.length > 0 ? item.servers[item.servers.length - 1].manageDate : ''], [sortOrder]);
  }
  if (sortBy === 'bsnSetup') {
    return orderBy(items, [item => item.bsnSetup && item.bsnSetup.network], [sortOrder]);
  }
  return orderBy(items, [sortBy], [sortOrder]);
}

function searchItems(items, search) {
  const normalizedSearch = search.toLowerCase();
  return filter(items, item => (
    (item.name ? item.name.toLowerCase().includes(normalizedSearch) : false)
    || (item.hostname ? item.hostname.toLowerCase().includes(normalizedSearch) : false)
    || (item.macAddress ? item.macAddress.toLowerCase().includes(normalizedSearch) : false)
    || (item.product ? item.product.toLowerCase().includes(normalizedSearch) : false)
    || (item.serialNumber ? item.serialNumber.toLowerCase().includes(normalizedSearch) : false)
    || (item.nextServer ? item.nextServer.name.toLowerCase().includes(normalizedSearch) : false)
    || (item.bsnSetup ? item.bsnSetup.network.toLowerCase().includes(normalizedSearch) : false)
  ))
}

export default function DeviceList() {
  const [sortBy, setSortBy] = useState('online');
  const [sortOrder, setSortOrder] = useState('desc');
  const [sortedItems, setSortedItems] = useState(null);
  const [search, setSearch] = useState('');
  const [filteredItems, setFilteredItems] = useState(null);
  const [managedOnly, setManagedOnly] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState({});
  const [items, setItems] = useState([]);

  const {data, updateUrl} = useFetchResource(`${onlineHubHost}/app/devices?managed=false`);

  useEffect(() => {
    setItems(filteredItems || sortedItems || (sortItems(data || [], sortBy, sortOrder)));
  }, [filteredItems, sortedItems, sortBy, sortOrder, data]);

  // Reset when data change
  useEffect(() => {
    setFilteredItems(null);
    setSortedItems(null);
    setSearch('');
    setSortOrder('desc');
    setSortBy('online');
  }, [data]);

  useEffect(() => {
    updateUrl(`${onlineHubHost}/app/devices?managed=${managedOnly ? 'true' : 'false'}`);
  }, [updateUrl, managedOnly]);

  function handleManageOnly(ev) {
    setManagedOnly(ev.target.checked);
  }

  function handleSortClick(newSortBy) {
    if (newSortBy === sortBy) {
      const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      setSortOrder(newSortOrder);
      setSortedItems(sortItems(items, sortBy, newSortOrder));
    } else {
      setSortBy(newSortBy);
      setSortedItems(sortItems(items, newSortBy, sortOrder));
    }
  }

  const handleSearchChange = debounce((value) => {
    const source = sortedItems || data || [];
    setFilteredItems(value.length ? searchItems(source, value) : null);
    setSearch(value);
  }, 500)

  function handleItemsDelete(ids) {
    fetch(`${onlineHubHost}/app/devices`, {
      method: 'DELETE',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ids})
    })
    .then((res) => {
      if (res.ok) {
        return res.json();
      }
      throw new Error('Unable to delete Devices');
    })
    .then((result) => {
      const newSortedItems = sortItems(reject(items, item => result.ids.includes(item.id)), sortBy, sortOrder);
      setSortedItems(newSortedItems);
      if (search) {
        setFilteredItems(searchItems(newSortedItems, search));
      }
      setSnackbarMessage({show: true, severity: 'success', text: `Device ${ids.length > 1 ? 'have' : 'has'} been deleted.`});
    })
    .catch((err) => {
      setSnackbarMessage({show: true, severity: 'error', text: `Unable to delete ${ids.length ? 'those devices' : 'this device'}.`})
      console.error(err);
    })
  }

  return (
    <div className="DeviceList">
      <div className="DeviceList-header">
        <FormControlLabel
          control={
            <Checkbox
              checked={managedOnly}
              onChange={handleManageOnly}
            />
          }
          label="Show managed"
        />
        <IconInput className="DeviceList-header-search" icon={<SearchIcon />} label="Search" onChange={handleSearchChange} />
      </div>
      <Table
        columns={managedOnly ? managedColumns : columns}
        items={items}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortClick={handleSortClick}
        deleteButton={!managedOnly}
        onDelete={handleItemsDelete}
      />
      <Snackbar
        open={snackbarMessage.show}
        autoHideDuration={3000}
        TransitionComponent={SlideTransition}
        onClose={() => setSnackbarMessage({...snackbarMessage, show: false})}
      >
        <Alert severity={snackbarMessage.severity} onClose={() => setSnackbarMessage({...snackbarMessage, show: false})}>
          {snackbarMessage.text}
        </Alert>
      </Snackbar>
    </div>
  );
}
